package devxertech.com.pages.blogs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.silk.components.icons.fa.FaMagnifyingGlass
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import devxertech.com.component.CommonDropdown
import devxertech.com.component.PaginationNumberComponent
import devxertech.com.pages.commons.OptionalInputIconField
import devxertech.com.theme.toSitePalette
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

private sealed class BlogListState {
    data object Loading : BlogListState()
    data class Loaded(val posts: List<SanityBlogPost>) : BlogListState()
    data object Failed : BlogListState()
}

@Composable
fun BlogsDescription() {
    val breakpoint = rememberBreakpoint()
    val palette = ColorMode.current.toSitePalette()
    var remoteState by remember {
        mutableStateOf<BlogListState>(initialPublishedPosts()?.let(BlogListState::Loaded) ?: BlogListState.Loading)
    }

    DisposableEffect(Unit) {
        var disposed = false
        fetchPublishedPosts()
            .then { posts ->
                if (!disposed) remoteState = BlogListState.Loaded(posts)
                Unit
            }
            .catch {
                if (!disposed && remoteState !is BlogListState.Loaded) remoteState = BlogListState.Failed
                null
            }
        onDispose { disposed = true }
    }

    var searchText by remember { mutableStateOf("") }
    var selectedCategory by remember { mutableStateOf(ALL_CATEGORIES_LABEL) }
    var currentPage by remember { mutableStateOf(1) }
    val posts = (remoteState as? BlogListState.Loaded)?.posts.orEmpty()
    val categories = posts.flatMap { it.categories }.distinct().sorted()
    val categoryOptions = listOf(ALL_CATEGORIES_LABEL) + categories

    fun resetPage() {
        currentPage = 1
    }

    val filteredPosts = posts.filter { post ->
        val matchesText = searchText.isBlank() ||
            post.title.contains(searchText, ignoreCase = true) ||
            post.excerpt.contains(searchText, ignoreCase = true)
        val matchesCategory = selectedCategory == ALL_CATEGORIES_LABEL ||
            post.categories.any { it.equals(selectedCategory, ignoreCase = true) }
        matchesText && matchesCategory
    }

    val rowsPerPage = 12
    val totalPages = (filteredPosts.size + rowsPerPage - 1) / rowsPerPage
    if (totalPages in 1..<currentPage) currentPage = 1
    val startIndex = ((currentPage - 1) * rowsPerPage).coerceAtMost(filteredPosts.size)
    val endIndex = (startIndex + rowsPerPage).coerceAtMost(filteredPosts.size)
    val visiblePosts = filteredPosts.subList(startIndex, endIndex)

    Column(
        modifier = Modifier
            .attrsModifier {
                attr("data-blog-state", when (remoteState) {
                    BlogListState.Loading -> "loading"
                    BlogListState.Failed -> "failed"
                    is BlogListState.Loaded -> "loaded"
                })
                attr("data-blog-total", posts.size.toString())
            }
            .fillMaxWidth()
            .padding(
                leftRight = when {
                    breakpoint == Breakpoint.MD || breakpoint == Breakpoint.LG -> 5.percent
                    breakpoint < Breakpoint.MD -> 8.percent
                    else -> 15.percent
                }
            )
            .margin(top = 50.px, bottom = 30.px),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(50.px)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .gap(if (breakpoint < Breakpoint.MD) 30.px else 0.px)
                .flexWrap(FlexWrap.Wrap),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            OptionalInputIconField(
                width = when {
                    breakpoint < Breakpoint.MD -> 100.percent
                    breakpoint == Breakpoint.MD -> 35.percent
                    window.screen.width in 900..1440 -> 25.percent
                    else -> 16.percent
                },
                value = searchText,
                onValueChange = {
                    searchText = it
                    resetPage()
                },
                inputType = InputType.Text,
                id = "blog-search",
                accessibleLabel = "Search blog posts",
                placeHolder = "Search for Blogs...",
                trailingIcon = {
                    FaMagnifyingGlass(
                        size = IconSize.SM,
                        modifier = Modifier.color(
                            palette.textFieldTextColor.rgb.copyf(alpha = 0.6f)
                        )
                    )
                },
            )

            CommonDropdown(
                accessibleLabel = "Filter blog posts by category",
                id = "blog-category-filter",
                data = categoryOptions,
                width = when {
                    breakpoint < Breakpoint.MD -> 100.percent
                    breakpoint == Breakpoint.MD -> 35.percent
                    window.screen.width in 900..1440 -> 25.percent
                    else -> 16.percent
                },
                selectedItem = selectedCategory,
                listWidth = 100.percent,
                onSelect = {
                    selectedCategory = it
                    resetPage()
                }
            )
        }

        when (remoteState) {
            BlogListState.Loading -> Row(
                modifier = Modifier.fillMaxWidth().gap(20.px).flexWrap(FlexWrap.Wrap),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                repeat(6) { BlogCardSkeleton() }
            }
            BlogListState.Failed -> BlogListMessage("Blog posts could not be loaded. Please try again later.")
            is BlogListState.Loaded -> {
                if (posts.isEmpty()) {
                    BlogListMessage("No published blog posts yet.")
                } else if (filteredPosts.isEmpty()) {
                    BlogListMessage("No blog posts match your search.")
                } else {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .gap(20.px)
                            .flexWrap(FlexWrap.Wrap),
                        horizontalArrangement = Arrangement.Start,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        visiblePosts.forEach { post ->
                            CommonInfoCard(
                                imageUrl = post.coverImageUrl,
                                imageAlt = post.coverImageAlt,
                                date = formatPublishedDate(post.publishedAt),
                                title = post.title,
                                linkText = "Read More",
                                linkUrl = "/blogs/${post.slug}"
                            )
                        }
                    }

                    if (totalPages > 1) {
                        Row(
                            modifier = Modifier.fillMaxWidth().margin(top = (-30).px),
                            horizontalArrangement = Arrangement.Center,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            PaginationNumberComponent(
                                id = "userPagination",
                                width = 100.percent,
                                currentPage = currentPage,
                                totalPages = totalPages,
                                onPageChange = { newPage -> currentPage = newPage },
                                sitePalette = palette
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun BlogListMessage(message: String) {
    val palette = ColorMode.current.toSitePalette()
    SpanText(
        text = message,
        modifier = Modifier.fillMaxWidth()
            .padding(topBottom = 30.px)
            .color(palette.commonSecondaryButtonColor.rgb)
    )
}

private const val ALL_CATEGORIES_LABEL = "Select Category"

internal fun formatPublishedDate(value: String): String {
    val date = value.substringBefore('T').split('-')
    if (date.size != 3) return value
    val month = date[1].toIntOrNull()?.let { monthNames.getOrNull(it - 1) } ?: return value
    val day = date[2].toIntOrNull() ?: return value
    return "$month $day, ${date[0]}"
}

private val monthNames = listOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
)
